/**
 * A book that moves while somebody is reading it.
 *
 * An edit is an EVENT — somebody committed a passage, at a moment — and that is
 * what the reader is told. The passages it names are the ones that flash. There
 * is nothing to derive: the book already records what was written.
 *
 * The decisions here are pure because none of them is visible in a rendered
 * page. Whether the words under a reader's eye stayed put, and whether somebody
 * mid-sentence had their draft taken away, show up only as behavior somebody
 * has to sit and reproduce.
 */

/**
 * How often a reader asks what has been committed.
 *
 * Slower than presence, because the two answer different urgencies: a roster is
 * about people arriving and leaving and is immediately wrong to look at when
 * stale, while a passage saved a few seconds ago is not yet worth interrupting
 * anybody's reading over. It is also the poll that leads to refetching a book,
 * so it is the one worth asking less often.
 */
export const BOOK_POLL_MS = 15 * 1000

/**
 * How long a committed passage stays marked.
 *
 * Long enough to be found by somebody whose eye was elsewhere on the page when
 * it arrived, and short enough that a book being worked on does not accumulate
 * marks until the whole of it is marked — which says nothing at all.
 */
export const FLASH_MS = 2500

/**
 * How far to scroll so the words under somebody's eye stay under it.
 *
 * A passage above the viewport that gains or loses a line moves everything
 * below it, so a reader who was not touching anything finds themselves
 * somewhere else. Measuring one block before and after the new words arrive and
 * correcting by the difference holds the page exactly, whatever changed above.
 *
 * Instant, never eased: this corrects a movement the reader did not ask for and
 * should never perceive. Animating it would draw the eye to the one thing that
 * is supposed to go unnoticed.
 */
export const keepPlace = (before: number, after: number) => {
  const drift = after - before
  /* Sub-pixel drift is rounding rather than movement, and scrolling by it would
     fight the reader's own inertial scroll for no visible gain. */
  return Math.abs(drift) < 1 ? 0 : drift
}

/**
 * Whether an update may be applied to what is on screen.
 *
 * A reader with a passage open is composing against the words as they read
 * them, and replacing those under an open draft would take away what they
 * typed. The save path refuses a write composed against a version that has
 * moved, so the collision itself is already answered — this only keeps the page
 * from pulling the ground out before they reach it.
 */
export const mayApply = (openBlockId: string | null) => openBlockId === null
